// ============================================================================
// JARVISTRAVEL - JOURNAL API (model-ready capture seam)
// Capturing into the journal — memories the traveler adds, and notes pinned to
// the trip / a day / a place. Demo resolves locally (notes persist in
// localStorage so they survive a reload); live talks to the journal endpoints.
// The Trip Journal story renders the results. JT-JOUR-301.
// ============================================================================

import { api, isDemoSession } from './client';
import { reportApiFailure } from './failure';
import { openDemoShare } from './share';
import { SHARED_JOURNAL_PREFIX } from '@/app/constants';
import type { UserMemory, JournalNote, JournalNoteScope } from '@/app/utils/tripJournal';

export interface AddMemoryInput {
  dayNumber: number;
  text: string;
}

/** The body actually posted for a memory. `time` is the traveller's local
 *  HH:MM at capture — the server cannot derive it, because its clock is UTC,
 *  and it has to come from the same clock as `dayNumber`, which this client
 *  already supplies (JAR-1272). */
type AddMemoryBody = AddMemoryInput & { time: string };

const nowTime = () => new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

// Demo persistence mirrors the notes seam below (JAR-296): memories survive a
// reload exactly like notes do, keyed per trip.
const memoriesKey = (tripId: string) => `jt.journal.memories.${tripId}`;

const readLocalMemories = (tripId: string): UserMemory[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(memoriesKey(tripId)) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const writeLocalMemories = (tripId: string, memories: UserMemory[]) => {
  try {
    localStorage.setItem(memoriesKey(tripId), JSON.stringify(memories));
  } catch {
    // storage full/unavailable — the in-memory state still renders this session
  }
};

/** List a trip's kept memories. Demo → localStorage; live → GET. */
export async function listJournalMemories(tripId: string): Promise<UserMemory[]> {
  if (isDemoSession()) return readLocalMemories(tripId);
  const result = await api.get<{ memories: UserMemory[] }>(`/trips/${encodeURIComponent(tripId)}/journal/memories`);
  return result.data?.memories ?? [];
}

/** Add a memory to a trip's journal. Demo → localStorage (survives reload,
 *  JAR-296); live → POST. */
export async function addJournalMemory(tripId: string, input: AddMemoryInput): Promise<UserMemory> {
  // Read the clock ONCE and use it for both paths. UserMemory.time is optional,
  // so reading it back off the object gives `string | undefined`; binding it
  // here keeps the type honest and makes it visible that the demo store and the
  // posted body carry the same value rather than two separate readings.
  const capturedAt = nowTime();
  const memory: UserMemory = {
    id: `mem-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    dayNumber: input.dayNumber,
    text: input.text,
    time: capturedAt,
  };
  if (isDemoSession()) {
    writeLocalMemories(tripId, [...readLocalMemories(tripId), memory]);
    return memory;
  }
  // Send the clock we just computed. The demo branch above has always used
  // this value; the live path built it and then posted a body without it, so
  // the server fell back to its own UTC clock and a memory captured at 21:00 in
  // Tokyo came back as 12:00 (JAR-1272). Same value, both paths.
  const body: AddMemoryBody = { ...input, time: capturedAt };
  const result = await api.post<{ memory: UserMemory }>(`/trips/${encodeURIComponent(tripId)}/journal/memories`, body);
  // The user authored this and can't reconstruct it — never report a failed save
  // as success (JAR-446). Propagate so the caller can offer a retry.
  if (result.error || !result.data?.memory) throw result.error ?? new Error('Failed to save the memory');
  return result.data.memory;
}

/** Rewrite a kept memory's text. Demo -> localStorage; live -> PUT.
 *
 *  Text only, because that is all core's route changes: a memory's day and time
 *  are fixed once written (core's UpdateMemoryRequest says why). The response
 *  carries the whole memory, so the caller replaces its copy rather than
 *  patching a field and hoping the rest still matches.
 *
 *  `PUT /v1/trips/{trip_id}/journal/memories/{memory_id}` — the memories pair,
 *  not the entries pair. The entries routes scope to a different entry type and
 *  answer 404 on every memory (JAR-2220). */
export async function updateJournalMemory(tripId: string, memoryId: string, text: string): Promise<UserMemory> {
  if (isDemoSession()) {
    const memories = readLocalMemories(tripId);
    const existing = memories.find((m) => m.id === memoryId);
    if (!existing) throw new Error('Memory not found');
    const updated = { ...existing, text };
    writeLocalMemories(tripId, memories.map((m) => (m.id === memoryId ? updated : m)));
    return updated;
  }
  const result = await api.put<{ memory: UserMemory }>(
    `/trips/${encodeURIComponent(tripId)}/journal/memories/${encodeURIComponent(memoryId)}`,
    { text },
  );
  // The traveller wrote this and cannot reconstruct it, so a failed save must
  // never read as success (JAR-446, the rule addJournalMemory above follows).
  if (result.error || !result.data?.memory) throw result.error ?? new Error('The memory could not be changed');
  return result.data.memory;
}

/** DELETE one journal memory or note, answering whether it's gone.
 *
 *  Answers false rather than throwing when the delete fails; the caller turns
 *  that into its own error, so a row is never hidden from a delete the server
 *  did not make. The failure is reported here with its classification, since a
 *  bare false reads the same for a 403, a 500 and a dropped connection.
 *
 *  Core answers 404 when the entry is already gone: another tab or device
 *  deleted it between our list read and this delete. That IS removal, so it
 *  counts as success rather than a failure whose retry re-404s forever (JAR-2176). */
async function deleteEntry(path: string, name: string): Promise<boolean> {
  const result = await api.del<{ ok: boolean }>(path);
  if (result.error && result.status === 404) return true;
  if (result.data?.ok) return true;
  reportApiFailure(`[journal] ${name} failed:`, result.error ?? new Error('The delete answered ok: false'));
  return false;
}

/** Remove a kept memory. Demo -> localStorage; live -> DELETE (see deleteEntry). */
export async function deleteJournalMemory(tripId: string, memoryId: string): Promise<boolean> {
  if (isDemoSession()) {
    writeLocalMemories(tripId, readLocalMemories(tripId).filter((m) => m.id !== memoryId));
    return true;
  }
  return deleteEntry(
    `/trips/${encodeURIComponent(tripId)}/journal/memories/${encodeURIComponent(memoryId)}`,
    'deleteJournalMemory',
  );
}

export interface JournalShareLink {
  url: string;
  token: string;
}

/** What core returns from POST /v1/trips/{trip_id}/share, narrowed to what this
 *  client reads. core deals in tokens and knows nothing about routes, so the FE
 *  builds the URL. The row also carries `expires_at`, `id`, `revoked` and
 *  `created_*`; none is read here, and a narrowed type that listed them would
 *  imply the FE does something with them (web-app#368 review, low). */
interface MintedShareLink {
  share_token: string;
}

/** The recipient's URL for a share token.
 *
 *  One place on purpose. Two branches build this, and they are exactly the pair
 *  that already drifted: the demo branch derives the link from the trip id, and
 *  it is where the leaked trip-id URL came from (JAR-446). Duplicating the
 *  template is how the live branch would quietly acquire the same bug
 *  (web-app#368 review, low). */
const buildShareUrl = (token: string) => `${window.location.origin}${SHARED_JOURNAL_PREFIX}${encodeURIComponent(token)}`;

/** Create a public, read-only share link for a trip's journal. Demo derives the
 *  link from the trip id (a stand-in — production must mint an OPAQUE token,
 *  never echo the trip id); live mints that token via the backend.
 *
 *  The live path used to POST /trips/{id}/journal/share, which core has never
 *  registered, so every live share failed with "couldn't create a link"
 *  (JAR-1477). The route core does register is POST /v1/trips/{trip_id}/share
 *  (main.go, JAR-1476), owner-only, and it answers 201 with the ShareLink row. */
export async function createJournalShareLink(tripId: string): Promise<JournalShareLink> {
  // Demo-only stand-in: derived from the trip id, never used in production.
  // Recorded as open, so the demo lists it and can stop it (JAR-1996).
  if (isDemoSession()) {
    openDemoShare(tripId, tripId);
    return { url: buildShareUrl(tripId), token: tripId };
  }
  const result = await api.post<MintedShareLink>(`/trips/${encodeURIComponent(tripId)}/share`, {});
  const token = result.data?.share_token;
  // Never fall back to a trip-id URL on failure: it wouldn't resolve and would
  // leak the id. Fail loudly so the caller says "couldn't create a link" instead
  // of copying a dead link that surfaces to the recipient (JAR-446).
  //
  // Reading `share_token` rather than a `url` is also what keeps the trip id out
  // of the link: core's token is opaque and the id never reaches the recipient.
  //
  // Reported here, once, for every caller: the trip Share modal's old mint
  // reported its failures, and moving it onto this function must not make them
  // silent (JAR-1543).
  if (result.error || !token) {
    const error = result.error ?? new Error('Failed to create the share link');
    reportApiFailure('[journal] createJournalShareLink failed:', error);
    throw error;
  }
  return { url: buildShareUrl(token), token };
}

// --- The recipient's side of a share link ----------------------------------

export interface SharedJournalStop {
  time?: string;
  name: string;
  location?: string;
  kind?: string;
}
export interface SharedJournalDay {
  day_number: number;
  date: string;
  /** A band ("chill", "balanced", "packed"), never a raw Fatigue Index number:
   *  core redacts it on the way out and the recipient never sees the figure. */
  fatigue?: string;
  stops: SharedJournalStop[];
}
export interface SharedJournalPlace {
  name: string;
  day_number: number;
  lat?: number;
  lng?: number;
}
export interface SharedJournalMemory {
  day_number: number;
  text: string;
  time?: string;
}
/** The redacted journal core builds for a share link. No budget, no spend, no
 *  receipts, no planning notes: what is absent here is absent by construction,
 *  because the recipient's page can only render what this carries. */
export interface SharedJournalView {
  trip_name: string;
  destination: string;
  country?: string;
  start_date?: string;
  end_date?: string;
  days: SharedJournalDay[];
  places: SharedJournalPlace[];
  memories: SharedJournalMemory[];
  /** Declared because core sends it, not because the page uses it: nothing
   *  renders the expiry today. Telling a recipient how long the link stays open
   *  is a copy decision nobody has made, so this stays a faithful record of the
   *  response rather than becoming invented UI (web-app#368 review, low). */
  expires_at: string;
}

/** Every way a share link can resolve. Kept as one closed set so the page has
 *  to render each of them: a recipient who lands on a dead link should be told
 *  which kind of dead it is, not shown the owner's "that trip isn't in your
 *  journal" (JAR-1477). */
export type SharedJournalResult =
  | { state: 'ok'; view: SharedJournalView }
  | { state: 'not-found' }
  | { state: 'expired' }
  | { state: 'revoked' }
  | { state: 'error' };

/** Load a shared journal by its token.
 *
 *  `auth: false` is load-bearing and not a tidiness choice. The recipient has
 *  no session, and the token IS the credential; core exempts exactly
 *  GET /v1/share/{token} from auth (middleware/auth.go isPublicSharePath).
 *  Sending an Authorization header here would make the request depend on a
 *  Clerk token the recipient will never have. */
export async function getSharedJournal(token: string): Promise<SharedJournalResult> {
  if (!token) return { state: 'not-found' };

  let result;
  try {
    result = await api.get<SharedJournalView>(`/share/${encodeURIComponent(token)}`, false);
  } catch {
    // This function RESOLVES every outcome, including this one. That is a
    // guarantee the recipient page depends on: it has no catch of its own, so
    // an escaping rejection would strand a recipient on "Loading" forever.
    // Made here, where it is one mock away from being tested, rather than
    // defended again at the call site as a branch nothing can reach
    // (web-app#368 review, low).
    return { state: 'error' };
  }

  if (!result.error && result.data) return { state: 'ok', view: result.data };

  // core answers 410 for BOTH revoked and expired, so the status alone cannot
  // pick the copy. The code in the error envelope is what separates them.
  const code = result.error?.code;
  if (code === 'REVOKED') return { state: 'revoked' };
  if (code === 'EXPIRED') return { state: 'expired' };
  if (code === 'NOT_FOUND' || result.status === 404) return { state: 'not-found' };
  // A 410 whose code we could not read is still definitely a dead link, and
  // "this link has expired" is the reading that does not accuse anyone of
  // revoking it.
  if (result.status === 410) return { state: 'expired' };
  return { state: 'error' };
}

// --- Notes (annotations on the trip / a day / a place) ----------------------

export interface SaveNoteInput {
  scope: JournalNoteScope;
  text: string;
}

const notesKey = (tripId: string) => `jt.journal.notes.${tripId}`;

/** Demo persistence: localStorage, so notes survive a reload. Corrupt or
 *  unavailable storage degrades to an empty list — never a crash. */
const readLocalNotes = (tripId: string): JournalNote[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(notesKey(tripId)) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const writeLocalNotes = (tripId: string, notes: JournalNote[]) => {
  try {
    localStorage.setItem(notesKey(tripId), JSON.stringify(notes));
  } catch {
    // storage full/unavailable — the in-memory state still renders this session
  }
};

/** List a trip's journal notes. Demo → localStorage; live → GET. */
export async function listJournalNotes(tripId: string): Promise<JournalNote[]> {
  if (isDemoSession()) return readLocalNotes(tripId);
  const result = await api.get<{ notes: JournalNote[] }>(`/trips/${encodeURIComponent(tripId)}/journal/notes`);
  return result.data?.notes ?? [];
}

/** Pin a note to the trip, a day, or a place. Demo → localStorage; live → POST. */
export async function addJournalNote(tripId: string, input: SaveNoteInput): Promise<JournalNote> {
  const note: JournalNote = {
    id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    scope: input.scope,
    text: input.text,
    createdAt: new Date().toISOString(),
    source: 'user',
  };
  if (isDemoSession()) {
    writeLocalNotes(tripId, [...readLocalNotes(tripId), note]);
    return note;
  }
  const result = await api.post<{ note: JournalNote }>(`/trips/${encodeURIComponent(tripId)}/journal/notes`, input);
  // User-authored content — a failed save must reject, not report success (JAR-446).
  if (result.error || !result.data?.note) throw result.error ?? new Error('Failed to save the note');
  return result.data.note;
}

/** Rewrite a note's text. Demo → localStorage; live → PUT. */
export async function updateJournalNote(tripId: string, noteId: string, text: string): Promise<JournalNote> {
  if (isDemoSession()) {
    const notes = readLocalNotes(tripId);
    const existing = notes.find((n) => n.id === noteId);
    if (!existing) throw new Error('Note not found');
    const updated = { ...existing, text };
    writeLocalNotes(tripId, notes.map((n) => (n.id === noteId ? updated : n)));
    return updated;
  }
  const result = await api.put<{ note: JournalNote }>(
    `/trips/${encodeURIComponent(tripId)}/journal/notes/${encodeURIComponent(noteId)}`,
    { text },
  );
  if (!result.data?.note) throw new Error('Note update failed');
  return result.data.note;
}

/** Remove a note. Demo → localStorage; live → DELETE (see deleteEntry). */
export async function deleteJournalNote(tripId: string, noteId: string): Promise<boolean> {
  if (isDemoSession()) {
    writeLocalNotes(tripId, readLocalNotes(tripId).filter((n) => n.id !== noteId));
    return true;
  }
  return deleteEntry(
    `/trips/${encodeURIComponent(tripId)}/journal/notes/${encodeURIComponent(noteId)}`,
    'deleteJournalNote',
  );
}

// --- Pivot places (unplanned finds) ------------------------------------------

import type { PivotPlace } from '@/app/utils/pivotPlaces';

export interface AddPivotInput {
  name: string;
  dayNumber: number;
  receiptId?: string;
  note?: string;
  lat?: number;
  lng?: number;
}

const pivotsKey = (tripId: string) => `jt.journal.pivots.${tripId}`;
const pivotDismissKey = (tripId: string) => `jt.journal.pivotDismissed.${tripId}`;

const readLocal = <T,>(key: string, fallback: T): T => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? 'null');
    return (parsed ?? fallback) as T;
  } catch {
    return fallback;
  }
};
const writeLocal = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full/unavailable — in-memory state still renders this session
  }
};

/** List a trip's kept finds. Demo → localStorage; live → GET. */
export async function listPivotPlaces(tripId: string): Promise<PivotPlace[]> {
  if (isDemoSession()) return readLocal<PivotPlace[]>(pivotsKey(tripId), []);
  const result = await api.get<{ pivots: PivotPlace[] }>(`/trips/${encodeURIComponent(tripId)}/journal/pivots`);
  return result.data?.pivots ?? [];
}

/** Keep an unplanned find. Demo → localStorage; live → POST. */
export async function addPivotPlace(tripId: string, input: AddPivotInput): Promise<PivotPlace> {
  const pivot: PivotPlace = {
    id: `pivot-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
    ...input,
  };
  if (isDemoSession()) {
    writeLocal(pivotsKey(tripId), [...readLocal<PivotPlace[]>(pivotsKey(tripId), []), pivot]);
    return pivot;
  }
  const result = await api.post<{ pivot: PivotPlace }>(`/trips/${encodeURIComponent(tripId)}/journal/pivots`, input);
  // A kept find is user intent — a failed save must reject so the prompt stays
  // offered for retry rather than silently vanishing (JAR-446).
  if (result.error || !result.data?.pivot) throw result.error ?? new Error('Failed to keep the find');
  return result.data.pivot;
}

/** "Not now" on a receipt-born prompt — remembered so it never re-asks. */
export async function dismissPivotPrompt(tripId: string, receiptId: string): Promise<boolean> {
  if (isDemoSession()) {
    const seen = readLocal<string[]>(pivotDismissKey(tripId), []);
    if (!seen.includes(receiptId)) writeLocal(pivotDismissKey(tripId), [...seen, receiptId]);
    return true;
  }
  const result = await api.post<{ ok: boolean }>(
    `/trips/${encodeURIComponent(tripId)}/journal/pivots/dismiss`,
    { receiptId },
  );
  return result.data?.ok ?? false;
}

/** Receipt ids whose prompts were dismissed. Demo → localStorage; live → GET. */
export async function listDismissedPivotPrompts(tripId: string): Promise<string[]> {
  if (isDemoSession()) return readLocal<string[]>(pivotDismissKey(tripId), []);
  const result = await api.get<{ receiptIds: string[] }>(
    `/trips/${encodeURIComponent(tripId)}/journal/pivots/dismissed`,
  );
  return result.data?.receiptIds ?? [];
}

/**
 * Remove every journal key a demo session writes for one trip — memories, notes,
 * pivot places and dismissed pivot prompts. Reuses the same key builders the
 * reads and writes above use, so the set stays in step if a key format changes
 * (JAR-1992). Best-effort: clearing demo data must not throw.
 */
export function clearJournalForTrip(tripId: string): void {
  for (const buildKey of [memoriesKey, notesKey, pivotsKey, pivotDismissKey]) {
    try {
      localStorage.removeItem(buildKey(tripId));
    } catch {
      /* storage unavailable — nothing to clear */
    }
  }
}
